import React, { useCallback, useMemo, useState } from 'react';
import { arrayOf, func, shape, number, string } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Table } from 'antd';
import { Card } from '@cards/Card';
import SelectContentModal from '@components/SelectContentModal';

import createColumns from './createColumns';
import '@pathways/cards/Pathways/CareTeamPermissions/styles.less';

function AddFormToRole({
  title,
  selectedPermissions,
  addPermissions,
  removePermission,
  updatePermissionType,
  customContent,
  allAvailableContent,
}) {
  const [isSelectFormVisible, setIsSelectFormVisible] = useState(false);

  const { t } = useTranslation();

  const columns = useMemo(() => createColumns(t, updatePermissionType, allAvailableContent), [
    t,
    updatePermissionType,
    allAvailableContent,
  ]);

  const dataSource = useMemo(
    () =>
      selectedPermissions.map(permission => ({
        ...permission,
        onDelete: () =>
          removePermission(permission.id ?? permission.form?.product_id ?? permission.form?.id),
      })),
    [selectedPermissions, removePermission],
  );

  const toggleSelectForm = useCallback(() => setIsSelectFormVisible(!isSelectFormVisible), [
    isSelectFormVisible,
  ]);

  const getNextPermissionId = useCallback(() => {
    const existingIds = selectedPermissions.map(p => p.id).filter(id => typeof id === 'number');
    return existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
  }, [selectedPermissions]);

  const handleAddForms = useCallback(
    (_, selectedForms) => {
      let nextId = getNextPermissionId();
      const newPermissions = selectedForms.map(form => ({
        id: nextId++,
        contentRef: form.contentRef || form.content,
        form,
        permissionType: 'send_notification',
        type: 'rule',
      }));
      addPermissions(newPermissions);
      setIsSelectFormVisible(false);
    },
    [addPermissions, getNextPermissionId],
  );

  const selectedFormIds = useMemo(() => {
    const ids = [];
    selectedPermissions
      .filter(p => p.type === 'rule' || p.form)
      .forEach(p => {
        if (p.id !== undefined) ids.push(p.id);
        if (p.form?.product_id !== undefined) ids.push(p.form.product_id);
        if (p.form?.id !== undefined) ids.push(p.form.id);
        if (p.form?.uuid) ids.push(p.form.uuid);
      });
    return ids;
  }, [selectedPermissions]);

  return (
    <>
      <Card.Full
        className="role-rules-card"
        extra={
          <>
            <Button icon="plus" type="dashed" onClick={toggleSelectForm}>
              {t('pathways:ProcedurePathways.wizard.careTeamRules.addForm')}
            </Button>
          </>
        }
        title={title}
      >
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          rowKey={record => record.id ?? record.form?.product_id ?? record.form?.id}
        />
      </Card.Full>
      {isSelectFormVisible ? (
        <SelectContentModal
          multiSelect={true}
          customContent={customContent.filter(
            c => c.audienceType === 'app-user' || c.audience_type === 'app-user',
          )}
          visible={isSelectFormVisible}
          onCancel={toggleSelectForm}
          onSubmit={handleAddForms}
          existingSelectedRowKeys={selectedFormIds}
        />
      ) : null}
    </>
  );
}

AddFormToRole.propTypes = {
  selectedPermissions: arrayOf(shape({})),
  title: string.isRequired,
  addPermissions: func.isRequired,
  removePermission: func.isRequired,
  updatePermissionType: func.isRequired,
  customContent: arrayOf(shape({})),
  allAvailableContent: arrayOf(shape({})),
  callToActionList: arrayOf(
    shape({
      id: number.isRequired,
      name: string.isRequired,
      type: string.isRequired,
      value: string.isRequired,
      audience: string.isRequired,
    }),
  ),
};

AddFormToRole.defaultProps = {
  selectedPermissions: [],
  customContent: [],
  allAvailableContent: [],
  callToActionList: [],
};

export default AddFormToRole;
